import { useState, useRef, useCallback, useEffect } from 'react';
import RedactionRenderModal from './RedactionRenderModal';
import WaveformStrip from './WaveformStrip';
import TimeField from '../media/TimeField';
import useWaveform from '../../hooks/useWaveform';
import { formatTimecode, round3 } from '../../domain/time';

// Smallest editable span (matches the backend's start<end + the waveform's
// minimum drag) so a typed edit can't collapse a range to zero.
const MIN_SPAN = 0.05;

/**
 * Redaction Studio — the in-viewer surface for marking and rendering
 * audio redactions on a media document. Rendered by MediaViewer between
 * the player and the transcript panels; invisible unless the
 * av_redaction feature flag is on (hook reports enabled=false).
 *
 * Three marking paths feed the same spec rows: transcript-segment ⛔
 * buttons (wired in the panels via onRedactRange), drag-to-create on
 * the timeline strip here, and the list's fine-grained editing.
 * Live preview is owned by useRedactions. See plans/AV_REDACTION_PLAN.md.
 */

const fmtTime = (s) => formatTimecode(s);

const KIND_META = {
  audio_bleep: { icon: '🔔', label: 'Bleep' },
  audio_mute: { icon: '🔇', label: 'Mute' },
  video_frame: { icon: '⬛', label: 'Blackout' },
};

// video_region's display derives from its region.style.
function kindMeta(r) {
  if (r.kind === 'video_region') {
    return r.region?.style === 'black'
      ? { icon: '◼️', label: 'Black box' }
      : { icon: '🌫️', label: 'Blur box' };
  }
  return KIND_META[r.kind] || { icon: '✂️', label: r.kind };
}

function RedactionStrip({ redact, currentTime, onSeek }) {
  const stripRef = useRef(null);
  const [draft, setDraft] = useState(null); // {start, end} in seconds while dragging
  const { duration, items } = redact;

  const timeAt = useCallback((clientX) => {
    const rect = stripRef.current.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return frac * duration;
  }, [duration]);

  const onMouseDown = (e) => {
    if (!duration || e.button !== 0) return;
    e.preventDefault();
    const start = timeAt(e.clientX);
    setDraft({ start, end: start });
    const onMove = (me) => setDraft({ start, end: timeAt(me.clientX) });
    const onUp = async (ue) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const end = timeAt(ue.clientX);
      setDraft(null);
      const lo = Math.min(start, end);
      const hi = Math.max(start, end);
      if (hi - lo >= 0.3) await redact.addRange(lo, hi, null);
      else onSeek?.(lo); // a click (no drag) just seeks
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!duration) {
    return <div className="rdx-strip rdx-strip--empty">Player loading…</div>;
  }
  const pct = (t) => `${(Math.min(t, duration) / duration) * 100}%`;
  return (
    <div
      className="rdx-strip"
      ref={stripRef}
      onMouseDown={onMouseDown}
      title="Drag to mark a redaction range; click to seek"
    >
      {items.map(r => (
        <div
          key={r.id}
          className={`rdx-strip-block rdx-strip-block--${r.kind}`}
          style={{ left: pct(r.time_start), width: pct(r.time_end - r.time_start) }}
          title={`${KIND_META[r.kind]?.label || r.kind} ${fmtTime(r.time_start)}–${fmtTime(r.time_end)}${r.label ? ` · ${r.label}` : ''}`}
        />
      ))}
      {draft && (
        <div
          className="rdx-strip-block rdx-strip-block--draft"
          style={{
            left: pct(Math.min(draft.start, draft.end)),
            width: pct(Math.abs(draft.end - draft.start)),
          }}
        />
      )}
      <div className="rdx-strip-playhead" style={{ left: pct(currentTime || 0) }} />
    </div>
  );
}

/** Editable start/end for a redaction: typed fields + playhead snap + seek. */
function RangeEditor({ r, redact, currentTime, onSeek }) {
  const duration = redact.duration || Infinity;

  const commitStart = (next) => {
    // Keep 0 ≤ start and at least MIN_SPAN below end.
    const start = round3(Math.min(Math.max(0, next), r.time_end - MIN_SPAN));
    if (Math.abs(start - r.time_start) > 0.0005) redact.updateItem(r.id, { time_start: start });
  };
  const commitEnd = (next) => {
    // Keep end ≤ duration and at least MIN_SPAN above start.
    const end = round3(Math.max(Math.min(next, duration), r.time_start + MIN_SPAN));
    if (Math.abs(end - r.time_end) > 0.0005) redact.updateItem(r.id, { time_end: end });
  };

  return (
    <div className="rdx-row-range-edit">
      <button
        type="button"
        className="rdx-snap"
        onClick={() => commitStart(currentTime || 0)}
        title="Set start to the current playhead position"
      >⇤</button>
      <TimeField value={r.time_start} onCommit={commitStart} ariaLabel="Redaction start time" />
      <span className="rdx-range-dash">–</span>
      <TimeField value={r.time_end} onCommit={commitEnd} ariaLabel="Redaction end time" />
      <button
        type="button"
        className="rdx-snap"
        onClick={() => commitEnd(currentTime || 0)}
        title="Set end to the current playhead position"
      >⇥</button>
      <button
        type="button"
        className="rdx-row-play"
        onClick={() => onSeek?.(Math.max(0, r.time_start - 1))}
        title="Play from just before this range"
      >▶</button>
    </div>
  );
}

function RedactionRow({ r, redact, currentTime, onSeek }) {
  const meta = kindMeta(r);
  const [label, setLabel] = useState(r.label || '');
  // Kind chip toggles within its modality: mute↔bleep for audio,
  // blur↔black for regions; full-frame blackouts have nothing to flip.
  const onToggleKind = () => {
    if (r.kind === 'audio_bleep' || r.kind === 'audio_mute') {
      redact.updateItem(r.id, {
        kind: r.kind === 'audio_bleep' ? 'audio_mute' : 'audio_bleep',
      });
    } else if (r.kind === 'video_region') {
      redact.updateItem(r.id, {
        region: {
          ...r.region,
          style: r.region?.style === 'black' ? 'blur' : 'black',
        },
      });
    }
  };
  return (
    <div className="rdx-row">
      <button
        className="rdx-row-kind"
        type="button"
        onClick={onToggleKind}
        disabled={r.kind === 'video_frame'}
        title={
          r.kind === 'video_frame'
            ? 'Full-frame blackout'
            : `${meta.label} — click to switch`
        }
      >
        {meta.icon} {meta.label}
      </button>
      <RangeEditor r={r} redact={redact} currentTime={currentTime} onSeek={onSeek} />
      <input
        className="rdx-row-label"
        value={label}
        placeholder="label (e.g. victim SSN)"
        maxLength={300}
        onChange={e => setLabel(e.target.value)}
        onBlur={() => {
          if ((r.label || '') !== label.trim()) {
            redact.updateItem(r.id, { label: label.trim() });
          }
        }}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      />
      {r.author_name && <span className="rdx-row-author" title="Marked by">{r.author_name}</span>}
      <button
        className="rdx-row-delete"
        type="button"
        onClick={() => redact.removeItem(r.id)}
        title="Remove this redaction"
      >
        ✕
      </button>
    </div>
  );
}

export default function RedactionStudio({ redact, caseId, documentId, currentTime, onSeek, speechSpans, isVideo = false, redactionOf = null, onOpenDocument = null }) {
  const [renderOpen, setRenderOpen] = useState(false);

  // "✓ added" confirmation for every new mark — a draw that captured
  // nothing must look different from one that worked (a silent black-box
  // draw was read as success during staging testing, 2026-06-12).
  const [addedNote, setAddedNote] = useState(null);
  useEffect(() => {
    if (!redact.lastAdded) return;
    setAddedNote(redact.lastAdded);
    const tid = setTimeout(() => setAddedNote(null), 6000);
    return () => clearTimeout(tid);
  }, [redact.lastAdded]);
  // Peaks fetch kicks off when the studio is first opened; the flat
  // strip serves until the envelope is ready (first open of a large
  // file pays a one-time background generation).
  const wave = useWaveform(documentId, redact.enabled && redact.mode && !redactionOf);
  if (!redact.enabled) return null;

  // This document is itself a redacted copy — a derived artifact with no
  // transcript and no editable spec. You edit redactions on the SOURCE (which
  // has the transcript + existing spec), then re-render supersedes this copy.
  if (redactionOf) {
    return (
      <div className="rdx-studio">
        <div className="rdx-bar">
          <button
            className="dv-transcript-toggle"
            type="button"
            disabled={!onOpenDocument}
            onClick={() => onOpenDocument?.(redactionOf)}
            title={onOpenDocument
              ? 'Open the original to adjust its redactions and re-render'
              : 'Open the original document to edit its redactions'}
          >
            <span>⛔ Edit redaction in original</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rdx-studio">
      <div className="rdx-bar">
        <button
          className={`dv-transcript-toggle ${redact.mode ? 'dv-transcript-toggle--active' : ''}`}
          type="button"
          onClick={() => redact.setMode(!redact.mode)}
          title="Mark ranges to mute or bleep in a shareable redacted copy"
        >
          <span>⛔ Redact</span>
          {redact.items.length > 0 && (
            <span className="dv-transcript-toggle-count">{redact.items.length}</span>
          )}
        </button>
        {redact.mode && (
          <>
            <label className="rdx-preview-toggle" title="Hear the redactions during playback — no render needed">
              <input
                type="checkbox"
                checked={redact.preview}
                onChange={e => redact.setPreview(e.target.checked)}
              />
              Preview
            </label>
            {isVideo && (
              <>
                <button
                  className={`dv-transcript-toggle ${redact.drawMode?.style === 'blur' ? 'dv-transcript-toggle--active' : ''}`}
                  type="button"
                  onClick={() => redact.setDrawMode(
                    redact.drawMode?.style === 'blur' ? null : { style: 'blur' })}
                  title="Pause on the frame, then drag a box over what to blur"
                >
                  🌫️ Blur box
                </button>
                <button
                  className={`dv-transcript-toggle ${redact.drawMode?.style === 'black' ? 'dv-transcript-toggle--active' : ''}`}
                  type="button"
                  onClick={() => redact.setDrawMode(
                    redact.drawMode?.style === 'black' ? null : { style: 'black' })}
                  title="Pause on the frame, then drag a box to black out"
                >
                  ◼️ Black box
                </button>
                <button
                  className="dv-transcript-toggle"
                  type="button"
                  onClick={() => redact.addFrameBlackout()}
                  title="Black out the entire frame for ~5s around the playhead"
                >
                  ⬛ Blackout frame
                </button>
              </>
            )}
            {redact.activeItems.length > 0 && (
              <span className="rdx-total">
                {redact.activeItems
                  .reduce((s, r) => s + (r.time_end - r.time_start), 0)
                  .toFixed(1)}s marked
              </span>
            )}
            <span className="rdx-hint">
              Drag on the strip, or use ⛔ on a transcript line
            </span>
            <button
              className="btn rdx-render-btn"
              type="button"
              disabled={redact.activeItems.length === 0}
              onClick={() => setRenderOpen(true)}
            >
              Render redacted copy
            </button>
          </>
        )}
      </div>
      {redact.mode && (
        <>
          {wave.status === 'ready' ? (
            <WaveformStrip
              wave={wave}
              redact={redact}
              speechSpans={speechSpans}
              onSeek={onSeek}
            />
          ) : (
            <>
              <RedactionStrip redact={redact} currentTime={currentTime} onSeek={onSeek} />
              {wave.status === 'generating' && (
                <div className="rdx-wave-pending">Building waveform…</div>
              )}
            </>
          )}
          {redact.error && <div className="rdx-error">{redact.error}</div>}
          {addedNote && (
            <div className="rdx-added-note">
              ✓ {kindMeta(addedNote).label} added at {fmtTime(addedNote.time_start)}–{fmtTime(addedNote.time_end)}
              {addedNote.kind !== 'video_frame' && addedNote.kind !== 'video_region'
                ? '' : ' — adjust its range on the strip'}
            </div>
          )}
          {redact.activeItems.length > 0 && (
            <div className="rdx-list">
              {redact.activeItems.map(r => (
                <RedactionRow key={r.id} r={r} redact={redact} currentTime={currentTime} onSeek={onSeek} />
              ))}
            </div>
          )}
        </>
      )}
      {renderOpen && (
        <RedactionRenderModal
          caseId={caseId}
          documentId={documentId}
          count={redact.activeItems.length}
          onClose={() => setRenderOpen(false)}
        />
      )}
    </div>
  );
}
